"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import { FooterContent, SocialLink } from "../types";
import EditableText from "../../../core/EditableText";
import {
  SocialLinks,
  BrandArea,
  Divider,
  Copyright,
  getSocialIcon,
} from "../BaseFooter";

// ============================================================
// CORPORATE FOOTER - Classic 4-column corporate layout
// ============================================================

interface CorporateFooterProps extends SectionContentProps {
  content: FooterContent;
}

export const CorporateFooter: React.FC<CorporateFooterProps> = ({
  content: propContent,
  onContentChange,
  isSelected,
}) => {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Dynamic colors with fallbacks
  const backgroundColor = content.backgroundColor || "#100063cc"; // slate-900
  const textColor = content.textColor || "#ffffff";
  const accentColor = content.accentColor || "#3b82f6"; // blue-500
  const secondaryTextColor = content.secondaryTextColor || "#94a3b8"; // slate-400

  // Default link groups
  const defaultLinkGroups = [
    {
      title: "Product",
      links: [
        { label: "Features", href: "#" },
        { label: "Pricing", href: "#" },
        { label: "Integrations", href: "#" },
        { label: "Changelog", href: "#" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About Us", href: "#" },
        { label: "Careers", href: "#" },
        { label: "Blog", href: "#" },
        { label: "Press", href: "#" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Documentation", href: "#" },
        { label: "Help Center", href: "#" },
        { label: "Community", href: "#" },
        { label: "Status", href: "#" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy Policy", href: "#" },
        { label: "Terms of Service", href: "#" },
        { label: "Cookie Policy", href: "#" },
        { label: "Security", href: "#" },
      ],
    },
  ];

  const linkGroups = content.linkGroups || defaultLinkGroups;

  const socialLinks: SocialLink[] = content.socialLinks || [
    { platform: "twitter", url: "#" },
    { platform: "linkedin", url: "#" },
    { platform: "github", url: "#" },
    { platform: "youtube", url: "#" },
  ];

  // Helper to handle link click for editing
  const handleLinkClick = (e: React.MouseEvent) => {
    if (isSelected) {
      e.preventDefault();
    }
  };

  return (
    <footer
      className={`${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        fontFamily,
        backgroundColor,
        color: textColor
      }}
      role="contentinfo"
      aria-label="Corporate footer"
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <BrandArea
              logo={content.logo}
              companyName={content.companyName}
              description={
                <EditableText
                  value={content.description || "Building the next generation of digital experiences through innovation and excellence."}
                  onChange={(v) => onContentChange?.({ ...content, description: v })}
                  as="span"
                  placeholder="Company description"
                />
              }
              accentColor={accentColor}
            />
            {/* Social Links */}
            <div className="mt-6">
              <SocialLinks
                links={socialLinks}
                size="sm"
                accentColor={accentColor}
              />
            </div>
          </div>

          {/* Link Columns */}
          {linkGroups.map((group, index) => (
            <div key={index}>
              <h4 className="text-white font-semibold mb-4" style={{ color: textColor }}>
                <EditableText
                  value={group.title}
                  onChange={(v) => {
                    const newGroups = [...linkGroups];
                    newGroups[index] = { ...newGroups[index], title: v };
                    onContentChange?.({ ...content, linkGroups: newGroups });
                  }}
                  as="span"
                  placeholder="Group Title"
                />
              </h4>
              <ul className="space-y-3" role="list">
                {group.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      onClick={handleLinkClick}
                      className="text-sm transition-colors duration-150 hover:opacity-80"
                      style={{ color: secondaryTextColor }}
                      target={(link as any).isExternal ? "_blank" : undefined}
                      rel={(link as any).isExternal ? "noopener noreferrer" : undefined}
                    >
                      <EditableText
                        value={link.label}
                        onChange={(v) => {
                          const newGroups = [...linkGroups];
                          const newLinks = [...group.links];
                          newLinks[linkIndex] = { ...link, label: v };
                          newGroups[index] = { ...group, links: newLinks };
                          onContentChange?.({ ...content, linkGroups: newGroups });
                        }}
                        as="span"
                        placeholder="Link Label"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Section */}
        <div
          className="border-t pt-10 mb-10"
          style={{ borderColor: `${textColor}20` }}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h4 className="text-white font-semibold mb-2" style={{ color: textColor }}>Stay in the loop</h4>
              <p className="text-sm" style={{ color: secondaryTextColor }}>
                Subscribe to our newsletter for product updates and news.
              </p>
            </div>
            <form
              className="flex w-full lg:w-auto gap-2"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 lg:w-64 px-4 py-2.5 rounded-lg text-sm placeholder:text-slate-500 focus:outline-none transition-colors"
                style={{
                  backgroundColor: `${textColor}10`,
                  borderColor: `${textColor}30`,
                  color: textColor
                }}
                aria-label="Email for newsletter"
              />
              <button
                type="submit"
                className="px-6 py-2.5 font-medium rounded-lg transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: accentColor,
                  color: "#ffffff"
                }}
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Divider */}
        <Divider color={`${textColor}20`} />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8">
          <Copyright
            text={
              <EditableText
                value={content.copyright || "© 2026 INSTACKS. All rights reserved."}
                onChange={(v) => onContentChange?.({ ...content, copyright: v })}
                as="span"
              />
            }
            color={secondaryTextColor}
          />

          {/* Bottom Links */}
          <nav aria-label="Footer legal links">
            <ul className="flex flex-wrap items-center gap-6 text-sm">
              {["Privacy", "Terms", "Cookies"].map((label, idx) => (
                <li key={idx}>
                  <a
                    href="#"
                    onClick={handleLinkClick}
                    className="transition-colors hover:opacity-80"
                    style={{ color: secondaryTextColor }}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default CorporateFooter;
